import React, { useState } from 'react'
import { useNls } from '../../../nls/nlsProvider'
import { AppDialog, BarcodeScannerShell } from '../../Common'
import { bookingCheckInByPlaceQr } from '../../../services/opsBookingsService'
import { formatMemberBookingError } from './memberBookingsErrorMapper'

export interface MemberScanPlaceQrModalProps {
  open: boolean
  onClose: () => void
  onSuccess?: () => void
}

function createOpId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID()
  }
  return `op_${Date.now()}`
}

export function MemberScanPlaceQrModal({
  open,
  onClose,
  onSuccess,
}: MemberScanPlaceQrModalProps): React.ReactElement {
  const { t } = useNls()
  const [errorMsg, setErrorMsg] = useState<string | null>(null)
  const [successMsg, setSuccessMsg] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const handleScan = async (scannedText: string) => {
    if (busy || !scannedText.trim()) return
    setBusy(true)
    setErrorMsg(null)
    setSuccessMsg(null)

    let token = scannedText.trim()
    if (token.includes('/pq/')) {
      token = token.split('/pq/')[1]?.split('?')[0] || token
    } else if (token.includes('/q/')) {
      token = token.split('/q/')[1]?.split('?')[0] || token
    }

    try {
      const opId = createOpId()
      await bookingCheckInByPlaceQr({ placeToken: token, opId })
      setSuccessMsg(t('NLSMemberBookingsScanSuccess', { place: token }))
      onSuccess?.()
    } catch (err: unknown) {
      setErrorMsg(formatMemberBookingError(err, t, { action: 'checkIn' }))
    } finally {
      setBusy(false)
    }
  }

  const handleDismissResult = () => {
    setErrorMsg(null)
    setSuccessMsg(null)
    onClose()
  }

  if (errorMsg || successMsg) {
    return (
      <AppDialog
        open={open}
        title={t('NLSMemberBookingsScanModalTitle')}
        onDismiss={handleDismissResult}
        actions={[
          {
            label: t('NLSCommonActionClose'),
            variant: 'primary',
            onClick: handleDismissResult,
          },
        ]}
      >
        <div className="memberBookingQrModal__content">
          {errorMsg && (
            <div className="memberLinkBookingsCard__error" role="alert">
              {errorMsg}
            </div>
          )}
          {successMsg && (
            <div className="memberLinkBookingsCard__success" role="status">
              {successMsg}
            </div>
          )}
        </div>
      </AppDialog>
    )
  }

  return (
    <BarcodeScannerShell
      open={open}
      onClose={onClose}
      onScan={(barcode) => {
        handleScan(barcode)
      }}
    />
  )
}
